import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';

interface AccountData {
  name: string | null;
  companyName: string | null;
  email: string | null;
}

interface OnboardingState {
  currentStep: 'environments' | 'teams' | 'services' | 'completed' | null;
  hasCreatedEnvironment: boolean;
  hasCreatedTeam: boolean;
  hasCreatedService: boolean;
  onboardingDismissed: boolean;
  onboardingCompletedOnBackend: boolean;
  checklistDismissedByAccount: Record<string, boolean>;
  
  setCurrentStep: (step: OnboardingState['currentStep']) => void;
  markEnvironmentCreated: () => void;
  markTeamCreated: () => void;
  markServiceCreated: (accountData: AccountData) => Promise<void>;
  completeOnboarding: (accountData: AccountData) => Promise<void>;
  skipOnboarding: (accountData: AccountData) => Promise<void>;
  initializeFromAccount: (onboardingCompleted: boolean) => void;
  dismissChecklistForAccount: (accountId: string) => void;
  isChecklistDismissedForAccount: (accountId: string) => boolean;
  
  shouldShowOnboarding: () => boolean;
  getNextStep: () => OnboardingState['currentStep'];
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      currentStep: 'environments',
      hasCreatedEnvironment: false,
      hasCreatedTeam: false,
      hasCreatedService: false,
      onboardingDismissed: false,
      onboardingCompletedOnBackend: false,
      checklistDismissedByAccount: {},

  setCurrentStep: (step) => {
    set({ currentStep: step });
  },

  markEnvironmentCreated: () => {
    set({ 
      hasCreatedEnvironment: true,
      currentStep: 'teams'
    });
  },

  markTeamCreated: () => {
    set({ 
      hasCreatedTeam: true,
      currentStep: 'services'
    });
  },

  markServiceCreated: async (accountData: AccountData) => {
    // Only proceed if onboarding is not already dismissed or completed on backend
    if (get().onboardingDismissed || get().onboardingCompletedOnBackend) {
      return;
    }
    
    set({ 
      hasCreatedService: true,
      currentStep: 'completed'
    });
    await get().completeOnboarding(accountData);
  },

  completeOnboarding: async (accountData: AccountData) => {
    // Only update if onboarding is not already dismissed or completed on backend
    if (get().onboardingDismissed || get().onboardingCompletedOnBackend) {
      return;
    }
    
    set({ currentStep: null, onboardingDismissed: true, onboardingCompletedOnBackend: true });
    try {
      await api.account.update({
        name: accountData.name,
        companyName: accountData.companyName,
        email: accountData.email,
        phone: null,
        onboardingCompleted: true
      });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  },

  skipOnboarding: async (accountData: AccountData) => {
    // Only update if onboarding is not already dismissed or completed on backend
    if (get().onboardingDismissed || get().onboardingCompletedOnBackend) {
      return;
    }
    
    set({ currentStep: null, onboardingDismissed: true, onboardingCompletedOnBackend: true });
    try {
      await api.account.update({
        name: accountData.name,
        companyName: accountData.companyName,
        email: accountData.email,
        phone: null,
        onboardingCompleted: true
      });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  },

  initializeFromAccount: (onboardingCompleted: boolean) => {
    // Set backend completion status and dismiss onboarding if already completed
    set({ 
      onboardingCompletedOnBackend: onboardingCompleted,
      currentStep: onboardingCompleted ? null : get().currentStep,
      onboardingDismissed: onboardingCompleted ? true : get().onboardingDismissed
    });
  },

  shouldShowOnboarding: () => {
    const state = get();
    return !state.onboardingDismissed && state.currentStep !== null && state.currentStep !== 'completed';
  },

  getNextStep: () => {
    const state = get();
    if (!state.hasCreatedEnvironment) return 'environments';
    if (!state.hasCreatedTeam) return 'teams';
    if (!state.hasCreatedService) return 'services';
    return 'completed';
  },

  dismissChecklistForAccount: (accountId: string) => {
    set((state) => ({
      checklistDismissedByAccount: {
        ...state.checklistDismissedByAccount,
        [accountId]: true,
      },
    }));
  },

  isChecklistDismissedForAccount: (accountId: string) => {
    return get().checklistDismissedByAccount[accountId] === true;
  },
    }),
    {
      name: 'onboarding-storage',
    }
  )
);
